import React from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Grid,
  GridItem,
  Card,
  CardBody,
  Stack,
  Divider,
  useColorModeValue,
  Icon,
  SimpleGrid
} from '@chakra-ui/react';
import { FaRocket, FaPalette, FaCog, FaChartLine } from 'react-icons/fa';
import {
  ChakraButton,
  ChakraBadge,
  ChakraHeading,
  ChakraText
} from '../components/atoms';

/**
 * Chakra UI コンポーネントのテストページ
 * 既存のTailwindコンポーネントとの並行運用を確認
 */
const ChakraTestPage = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box bg={bgColor} minH="100vh" py={10}>
      <Container maxW="7xl">
        <VStack spacing={12} align="stretch">
          {/* ヘッダーセクション */}
          <Box textAlign="center">
            <ChakraBadge variant="premium" mb={4}>
              Chakra UI テストページ
            </ChakraBadge>
            <ChakraHeading variant="section" size="2xl" mb={4}>
              IRORI Tourism - Chakra UI コンポーネント
            </ChakraHeading>
            <ChakraText fontSize="lg" color="gray.600" maxW="3xl" mx="auto">
              既存のTailwind CSSとChakra UIの並行運用をテストし、
              スムーズな移行を確認します。
            </ChakraText>
          </Box>

          {/* ボタンサンプル */}
          <Card bg={cardBg}>
            <CardBody>
              <ChakraHeading size="lg" mb={6}>
                ボタンコンポーネント
              </ChakraHeading>
              <SimpleGrid columns={[1, 2, 4]} spacing={4}>
                <ChakraButton variant="primary" size="md">
                  プライマリー
                </ChakraButton>
                <ChakraButton variant="secondary" size="md">
                  セカンダリー
                </ChakraButton>
                <ChakraButton variant="outline" size="md">
                  アウトライン
                </ChakraButton>
                <ChakraButton variant="ghost" size="md">
                  ゴースト
                </ChakraButton>
              </SimpleGrid>
              
              <Divider my={6} />
              
              <ChakraText mb={4} fontWeight="medium">
                サイズバリエーション:
              </ChakraText>
              <HStack spacing={4} flexWrap="wrap">
                <ChakraButton size="sm">小</ChakraButton>
                <ChakraButton size="md">中</ChakraButton>
                <ChakraButton size="lg">大</ChakraButton>
              </HStack>
            </CardBody>
          </Card>

          {/* バッジサンプル */}
          <Card bg={cardBg}>
            <CardBody>
              <ChakraHeading size="lg" mb={6}>
                バッジコンポーネント
              </ChakraHeading>
              <VStack align="start" spacing={4}>
                <HStack spacing={3} flexWrap="wrap">
                  <ChakraBadge variant="premium">プレミアム</ChakraBadge>
                  <ChakraBadge colorScheme="brand">ブランド</ChakraBadge>
                  <ChakraBadge colorScheme="gold">ゴールド</ChakraBadge>
                  <ChakraBadge colorScheme="green">新着</ChakraBadge>
                  <ChakraBadge colorScheme="blue">人気</ChakraBadge>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* テキストサンプル */}
          <Card bg={cardBg}>
            <CardBody>
              <ChakraHeading size="lg" mb={6}>
                テキストコンポーネント
              </ChakraHeading>
              <VStack align="start" spacing={4}>
                <ChakraText fontSize="xl" fontWeight="bold" color="irori.600">
                  ブランドカラーのテキスト（XL・太字）
                </ChakraText>
                <ChakraText fontSize="lg" color="gray.700">
                  大きめのテキスト（LG・標準）
                </ChakraText>
                <ChakraText fontSize="md" color="gray.600">
                  標準サイズのテキスト（MD・標準）
                </ChakraText>
                <ChakraText fontSize="sm" color="gray.500">
                  小さめのテキスト（SM・標準）
                </ChakraText>
              </VStack>
            </CardBody>
          </Card>

          {/* レイアウトサンプル */}
          <Card bg={cardBg}>
            <CardBody>
              <ChakraHeading size="lg" mb={6}>
                レイアウトサンプル
              </ChakraHeading>
              <Grid templateColumns={["1fr", "1fr 1fr", "repeat(4, 1fr)"]} gap={6}>
                <GridItem>
                  <Box p={6} bg="irori.50" borderRadius="lg" textAlign="center">
                    <Icon as={FaRocket} boxSize={8} color="irori.500" mb={3} />
                    <ChakraText fontWeight="bold" mb={2}>高速</ChakraText>
                    <ChakraText fontSize="sm" color="gray.600">
                      最適化されたパフォーマンス
                    </ChakraText>
                  </Box>
                </GridItem>
                <GridItem>
                  <Box p={6} bg="gold.50" borderRadius="lg" textAlign="center">
                    <Icon as={FaPalette} boxSize={8} color="gold.500" mb={3} />
                    <ChakraText fontWeight="bold" mb={2}>美しい</ChakraText>
                    <ChakraText fontSize="sm" color="gray.600">
                      モダンなデザインシステム
                    </ChakraText>
                  </Box>
                </GridItem>
                <GridItem>
                  <Box p={6} bg="blue.50" borderRadius="lg" textAlign="center">
                    <Icon as={FaCog} boxSize={8} color="blue.500" mb={3} />
                    <ChakraText fontWeight="bold" mb={2}>カスタマイズ</ChakraText>
                    <ChakraText fontSize="sm" color="gray.600">
                      柔軟なテーマシステム
                    </ChakraText>
                  </Box>
                </GridItem>
                <GridItem>
                  <Box p={6} bg="green.50" borderRadius="lg" textAlign="center">
                    <Icon as={FaChartLine} boxSize={8} color="green.500" mb={3} />
                    <ChakraText fontWeight="bold" mb={2}>拡張性</ChakraText>
                    <ChakraText fontSize="sm" color="gray.600">
                      段階的な導入が可能
                    </ChakraText>
                  </Box>
                </GridItem>
              </Grid>
            </CardBody>
          </Card>

          {/* 統合テストセクション */}
          <Card bg={cardBg}>
            <CardBody>
              <ChakraHeading size="lg" mb={6}>
                TailwindとChakra UI統合テスト
              </ChakraHeading>
              <ChakraText mb={6} color="gray.600">
                既存のTailwindスタイルとChakra UIコンポーネントが
                同じページで正常に動作することを確認します。
              </ChakraText>
              
              {/* Tailwindスタイルのボックス */}
              <div className="mb-6 p-6 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-white">
                <h3 className="text-xl font-bold mb-2">Tailwind CSS スタイル</h3>
                <p className="text-orange-100">
                  このボックスは既存のTailwindクラスでスタイリングされています。
                </p>
                <button className="mt-4 px-4 py-2 bg-white text-orange-600 rounded hover:bg-orange-50 transition-colors">
                  Tailwindボタン
                </button>
              </div>

              {/* Chakra UIスタイルのボックス */}
              <Box
                p={6}
                bgGradient="linear(to-r, irori.500, gold.500)"
                borderRadius="lg"
                color="white"
              >
                <ChakraHeading size="lg" mb={2} color="white">
                  Chakra UI スタイル
                </ChakraHeading>
                <ChakraText color="whiteAlpha.900" mb={4}>
                  このボックスはChakra UIのスタイルプロパティでスタイリングされています。
                </ChakraText>
                <ChakraButton variant="outline" colorScheme="whiteAlpha">
                  Chakraボタン
                </ChakraButton>
              </Box>
            </CardBody>
          </Card>

          {/* アクションボタン */}
          <Box textAlign="center">
            <HStack spacing={4} justify="center" flexWrap="wrap">
              <ChakraButton
                variant="primary"
                size="lg"
                onClick={() => window.location.href = '/'}
              >
                ホームに戻る
              </ChakraButton>
              <ChakraButton
                variant="secondary"
                size="lg"
                onClick={() => window.open('https://chakra-ui.com', '_blank')}
              >
                Chakra UI ドキュメント
              </ChakraButton>
            </HStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default ChakraTestPage;